<template>
  <v-dialog :value="dialog" @input="$emit('update:dialog', $event)" max-width="600px" persistent>
    <v-card v-if="group">
      <v-card-title>
        <span class="headline">Registrar Amonestación</span>
        <v-spacer></v-spacer>
        <v-icon @click="closeDialog" class="cursor-pointer">mdi-close</v-icon>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="validForm" lazy-validation>
          <!-- Información del grupo -->
          <v-row>
            <v-col cols="12">
              <v-alert type="info" outlined>
                <strong>Grupo {{ group.group_number }}</strong> - {{ group.players.join(', ') }}
              </v-alert>
            </v-col>
          </v-row>

          <!-- Seleccionar jugador afectado -->
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="form.player"
                :items="group.players"
                label="Jugador Afectado"
                outlined
                dense
                :rules="[v => !!v || 'El jugador es requerido']"
              ></v-select>
            </v-col>
          </v-row>

          <!-- Tipo de amonestación -->
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="form.warning_type"
                :items="warningCatalog"
                item-text="name"
                item-value="code"
                label="Tipo de Amonestación"
                outlined
                dense
                :rules="[v => !!v || 'El tipo es requerido']"
                @change="updateWarningDetails"
              >
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center">
                    <span class="mr-2">{{ item.name }}</span>
                    <v-chip small :color="getSeverityColor(item.severity)" text-color="white">
                      {{ item.severity }} strike{{ item.severity > 1 ? 's' : '' }}
                    </v-chip>
                  </div>
                </template>
                <template v-slot:selection="{ item }">
                  <span>{{ item.name }}</span>
                  <v-chip small :color="getSeverityColor(item.severity)" text-color="white" class="ml-2">
                    {{ item.severity }} strike{{ item.severity > 1 ? 's' : '' }}
                  </v-chip>
                </template>
              </v-select>
            </v-col>
          </v-row>

          <!-- Descripción de la amonestación -->
          <v-row>
            <v-col cols="12">
              <div v-if="selectedWarning" class="mb-3 pa-3 grey lighten-4 rounded">
                <div class="caption font-weight-bold">Descripción</div>
                <div class="body-2">{{ selectedWarning.description }}</div>
              </div>
            </v-col>
          </v-row>

          <!-- Hoyo donde ocurrió -->
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.hole"
                type="number"
                label="Hoyo"
                outlined
                dense
                min="1"
                max="18"
                :rules="[v => !v || (v >= 1 && v <= 18) || 'Hoyo debe estar entre 1 y 18']"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="form.time"
                label="Hora"
                outlined
                dense
                disabled
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Notas adicionales -->
          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="form.notes"
                label="Notas Adicionales (Opcional)"
                outlined
                dense
                rows="3"
                counter
                maxlength="200"
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Resumen de strikes -->
          <v-row v-if="selectedWarning">
            <v-col cols="12">
              <v-alert type="warning" outlined>
                <div class="font-weight-bold">Strikes a Aplicar: {{ selectedWarning.severity }}</div>
                <div class="caption mt-1">El jugador acumulará {{ selectedWarning.severity }} strike(s) por esta amonestación</div>
              </v-alert>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog">Cancelar</v-btn>
        <v-btn color="red" text @click="submitWarning" :disabled="!validForm">
          <v-icon left>mdi-alert-circle</v-icon>
          Registrar Amonestación
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'WarningDialog',
  props: {
    dialog: Boolean,
    group: {
      type: Object,
      default: () => ({
        group_number: 0,
        players: []
      })
    }
  },
  data () {
    return {
      validForm: false,
      form: {
        player: '',
        warning_type: '',
        hole: '',
        time: '',
        notes: ''
      },
      // MOCK DATA - Catálogo de amonestaciones
      warningCatalog: [
        {
          code: 'SLOW_PACE',
          name: 'Ritmo Lento',
          description: 'El grupo avanza más lentamente de lo permitido',
          category: 'pace',
          severity: 1
        },
        {
          code: 'DAMAGE',
          name: 'Daño al Campo',
          description: 'Daño causado al campo de juego',
          category: 'damage',
          severity: 1
        },
        {
          code: 'ATTIRE',
          name: 'Violación de Código de Vestimenta',
          description: 'Incumplimiento de las reglas de vestimenta del club',
          category: 'attire',
          severity: 1
        },
        {
          code: 'CONDUCT',
          name: 'Conducta Inapropiada',
          description: 'Comportamiento que viola las reglas de cortesía del golf',
          category: 'conduct',
          severity: 2
        },
        {
          code: 'RULES_VIOLATION',
          name: 'Violación de Reglas de Juego',
          description: 'Incumplimiento de las reglas oficiales del golf',
          category: 'rules',
          severity: 1
        },
        {
          code: 'VERBAL_WARNING',
          name: 'Amonestación Verbal',
          description: 'Advertencia sin aplicación de strikes',
          category: 'warning',
          severity: 0
        }
      ]
    }
  },
  computed: {
    selectedWarning () {
      return this.warningCatalog.find(w => w.code === this.form.warning_type)
    }
  },
  watch: {
    dialog (newVal) {
      if (newVal) {
        this.form.time = this.getCurrentTime()
        this.form.hole = this.group.current_hole || ''
      } else {
        this.resetForm()
      }
    }
  },
  methods: {
    getCurrentTime () {
      const now = new Date()
      return now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
    },
    getSeverityColor (severity) {
      const colors = {
        0: 'grey',
        1: 'orange',
        2: 'red'
      }
      return colors[severity] || 'grey'
    },
    updateWarningDetails () {
      // Se puede usar para actualizar otros campos basados en el tipo
    },
    closeDialog () {
      this.$emit('update:dialog', false)
    },
    submitWarning () {
      if (this.$refs.form.validate()) {
        const warning = {
          type: this.selectedWarning.name,
          code: this.form.warning_type,
          player: this.form.player,
          hole: this.form.hole,
          time: this.form.time,
          notes: this.form.notes,
          strikes: this.selectedWarning.severity
        }
        this.$emit('save', warning)
        this.resetForm()
      }
    },
    resetForm () {
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
      this.form = {
        player: '',
        warning_type: '',
        hole: '',
        time: '',
        notes: ''
      }
      this.validForm = false
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
